import { createSlice } from '@reduxjs/toolkit';
import {
  HRC_CHAT_AGENT_ONLINE_STATUS,
  CURRENT_CHAT_REF_ID_KEY,
  ChatBucket,
  IChatListItem,
  LoanAndUserDetails,
  WHATSAPP_CHATBOT_PAGINATE_API_BATCH_SIZE,
  WhatsappChatbotMessages,
  AGENT_STATUS,
  ReplySendingStatus,
  AppendNewWhatsappChatbotMessagePayload,
  CHAT_MESSAGE_TAG,
  quickFiltersDefaultValues,
  ITriggerTemplateDetails,
  IImageData
} from 'src/pages/WhatsappChatbot/types';
import {
  addChatItemToChatBucket,
  removeChatItemFromChatBucket,
  setChatList
} from '../utils/whatsAppchatbotUtils';

interface IWhatsAppChatbotState {
  chatBuckets: ChatBucket;
  messages: Array<WhatsappChatbotMessages>;
  messageIdToMessageMap: Record<string, WhatsappChatbotMessages>;
  hasMore: boolean;
  currentChat: IChatListItem | null;
  showErrorPage: boolean;
  searchQuery: string;
  selectedChatLoanAndUserDetails: LoanAndUserDetails | null;
  chatsCount: number;
  isAgentOnline: boolean;
  showHandleCurrentChatDeletion: boolean;
  firebaseSignInLoading: boolean;
  filteredChats: Array<IChatListItem>;
  replySendingStatus: ReplySendingStatus;
  shouldScrollToBottom: number;
  quickFilters: Record<CHAT_MESSAGE_TAG, boolean>;
  filtersApplied: boolean;
  templateTriggeredDetails: ITriggerTemplateDetails[];
  isFileUploaded: boolean;
  uploadedFileData: IImageData | null;
  referenceIdToChatMap: Record<string, IChatListItem>;
}

const initialState = {
  chatBuckets: {},
  currentChat: null,
  messages: [],
  messageIdToMessageMap: {},
  hasMore: false,
  showErrorPage: false,
  searchQuery: '',
  selectedChatLoanAndUserDetails: null,
  chatsCount: 0,
  isAgentOnline: false,
  showHandleCurrentChatDeletion: false,
  firebaseSignInLoading: false,
  filteredChats: [],
  replySendingStatus: ReplySendingStatus.LOADING,
  shouldScrollToBottom: 0,
  isScrolledToBottom: false,
  quickFilters: {
    ...quickFiltersDefaultValues
  },
  filtersApplied: false,
  templateTriggeredDetails: [],
  isFileUploaded: false,
  uploadedFileData: null,
  referenceIdToChatMap: {}
} as IWhatsAppChatbotState;

const whatsappChatbotSlice = createSlice({
  name: 'whatsappChatbotSlice',
  initialState,
  reducers: {
    setNewWhatsappChatbotMessage(state, action) {
      const { referenceId, messages } = action.payload;
      const messageListReverse = messages?.reverse();
      if (state?.currentChat?.referenceId !== referenceId) return;

      if (messageListReverse?.length === 0) {
        state.hasMore = false;
        return;
      } else if (messageListReverse?.length < WHATSAPP_CHATBOT_PAGINATE_API_BATCH_SIZE) {
        state.hasMore = false;
      } else if (
        messageListReverse?.length === WHATSAPP_CHATBOT_PAGINATE_API_BATCH_SIZE &&
        state.hasMore === false
      ) {
        state.hasMore = true;
      }

      const resMessageIdToMessageMap = {} as Record<string, WhatsappChatbotMessages>;
      messageListReverse?.forEach((message: WhatsappChatbotMessages) => {
        resMessageIdToMessageMap[message.messageId] = message;
      });
      state.messageIdToMessageMap = resMessageIdToMessageMap;
      state.messages = messageListReverse;
      state.replySendingStatus = ReplySendingStatus.IDLE;
    },
    appendNewWhatsappChatbotMessage(state, action) {
      const { referenceId, messages }: AppendNewWhatsappChatbotMessagePayload = action.payload;
      const messageListReverse = messages?.reverse();
      if (state?.currentChat?.referenceId !== referenceId) return;

      if (messageListReverse?.length === 0) {
        state.hasMore = false;
        return;
      } else if (messageListReverse?.length < WHATSAPP_CHATBOT_PAGINATE_API_BATCH_SIZE) {
        state.hasMore = false;
      }

      const resMessageIdToMessageMap = {} as Record<string, WhatsappChatbotMessages>;
      messageListReverse?.forEach((message: WhatsappChatbotMessages) => {
        resMessageIdToMessageMap[message.messageId] = message;
      });

      state.messageIdToMessageMap = { ...state.messageIdToMessageMap, ...resMessageIdToMessageMap };
      state.messages = [...messageListReverse, ...state.messages];
    },
    setUpdateWhatsappChatbotMessage(state, action) {
      const { referenceId, messages } = action.payload;
      const messageListReverse = messages?.reverse();
      if (referenceId !== state?.currentChat?.referenceId || messageListReverse?.length === 0)
        return;
      const appendNewMsgs = [] as Array<WhatsappChatbotMessages>;
      const updatedMessageIdToMessageMap = {} as Record<string, WhatsappChatbotMessages>;
      let update = false;

      messageListReverse?.forEach((message: WhatsappChatbotMessages) => {
        if (!(message.messageId in state.messageIdToMessageMap)) {
          updatedMessageIdToMessageMap[message.messageId] = message;
          appendNewMsgs.push(message);
          update = true;
        } else if (
          state.messageIdToMessageMap[message.messageId].deliveryStatus !== message.deliveryStatus
        ) {
          updatedMessageIdToMessageMap[message.messageId] = message;
          update = true;
        }
      });

      if (update) {
        state.messageIdToMessageMap = {
          ...state.messageIdToMessageMap,
          ...updatedMessageIdToMessageMap
        };
      }

      if (appendNewMsgs.length) {
        state.messages = [...state.messages, ...appendNewMsgs];
        state.shouldScrollToBottom = state.shouldScrollToBottom + 1;
      }
    },
    setHasMore(state, action) {
      state.hasMore = action.payload;
    },
    setShowErrorPage(state, action) {
      state.showErrorPage = action?.payload;
    },
    addWhatsappChatbotMessage(state, action) {
      const { referenceId, message } = action.payload;
      if (state?.currentChat?.referenceId !== referenceId) return;
      state.messageIdToMessageMap[message.messageId] = message;
      state.messages = [...state.messages, message];
      state.replySendingStatus = ReplySendingStatus.IDLE;
      state.shouldScrollToBottom = state.shouldScrollToBottom + 1;
    },
    handleCurrentChatDeletion: state => {
      state.hasMore = false;
      const chatItem = state.currentChat;
      state.currentChat = null;
      state.messages = [];
      state.messageIdToMessageMap = {};
      if (chatItem) {
        removeChatItemFromChatBucket(state.chatBuckets, chatItem);
        if (chatItem.referenceId && state.referenceIdToChatMap[chatItem.referenceId]) {
          delete state.referenceIdToChatMap[chatItem.referenceId];
        }
      }
      state.filteredChats = setChatList(state.chatBuckets);
      state.showHandleCurrentChatDeletion = false;
    },
    setChatBuckets: (state, action) => {
      const { addedCases, modifiedCases, removedCases, reset } = action.payload;
      if (reset) {
        state.chatBuckets = {};
        state.referenceIdToChatMap = {};
      }
      removedCases.forEach((chatItem: IChatListItem) => {
        if (chatItem.referenceId === state?.currentChat?.referenceId) {
          state.currentChat = null;
          state.showHandleCurrentChatDeletion = true;
        } else {
          removeChatItemFromChatBucket(state.chatBuckets, chatItem);
        }
        if (chatItem.referenceId && state.referenceIdToChatMap[chatItem.referenceId]) {
          delete state.referenceIdToChatMap[chatItem.referenceId];
        }
      });
      addedCases.forEach((chatItem: IChatListItem) => {
        addChatItemToChatBucket(state.chatBuckets, chatItem);
        if (chatItem.referenceId) {
          state.referenceIdToChatMap[chatItem.referenceId] = chatItem;
        }
      });
      modifiedCases.forEach((chatItem: IChatListItem) => {
        if (chatItem.referenceId === state?.currentChat?.referenceId) {
          state.currentChat = chatItem;
        }
        removeChatItemFromChatBucket(state.chatBuckets, chatItem);
        addChatItemToChatBucket(state.chatBuckets, chatItem);

        if (chatItem.referenceId) {
          state.referenceIdToChatMap[chatItem.referenceId] = chatItem;
        }
      });
      state.chatsCount = 0;
      state.filteredChats = setChatList(state.chatBuckets);
      if (state.filteredChats?.length > 0) {
        state.chatsCount = state.filteredChats.length;
      }
    },
    setCurrentChat(state, action) {
      state.hasMore = false;
      state.currentChat = action.payload;
      state.messages = [];
      state.messageIdToMessageMap = {};
      state.selectedChatLoanAndUserDetails = null;
    },
    restoreCurrentChatFromReferenceId(state, action) {
      const referenceId = action.payload;
      if (referenceId && state.referenceIdToChatMap[referenceId]) {
        state.currentChat = state.referenceIdToChatMap[referenceId];
        state.hasMore = false;
        state.messages = [];
        state.messageIdToMessageMap = {};
        state.selectedChatLoanAndUserDetails = null;
      }
    },
    setInitializeReferenceIdMap(state) {
      if (state.filteredChats && state.filteredChats.length > 0) {
        state.filteredChats.forEach((chatItem: IChatListItem) => {
          if (chatItem.referenceId) {
            state.referenceIdToChatMap[chatItem.referenceId] = chatItem;
          }
        });
      }
    },
    setSearchQuery(state, action) {
      state.searchQuery = action.payload;
    },
    setSelectedChatLoanAndUserDetails(state, action) {
      state.selectedChatLoanAndUserDetails = action.payload;
    },
    setIsAgentOnline(state, action) {
      state.isAgentOnline = action.payload;
    },
    setFirebaseSignInLoading(state, action) {
      state.firebaseSignInLoading = action.payload;
    },
    setReplySendingStatus(state, action) {
      state.replySendingStatus = action.payload;
    },
    setShouldScrollToBottom(state) {
      state.shouldScrollToBottom = state.shouldScrollToBottom + 1;
    },
    setQuickFilters(state, action) {
      state.quickFilters = {
        ...state.quickFilters,
        ...action.payload
      };
      state.filtersApplied = Object.keys(state.quickFilters).some(key => {
        return state.quickFilters[key as CHAT_MESSAGE_TAG];
      });
    },
    resetQuickFilters(state) {
      state.quickFilters = quickFiltersDefaultValues;
      state.filtersApplied = false;
    },
    setTemplateTriggeredDetails(state, action) {
      state.templateTriggeredDetails = action.payload;
    },
    setIsFileUploaded(state, action) {
      state.isFileUploaded = action.payload;
    },
    setUploadedFileData(state, action) {
      state.uploadedFileData = action.payload;
    }
  }
});

export const {
  setNewWhatsappChatbotMessage,
  appendNewWhatsappChatbotMessage,
  setUpdateWhatsappChatbotMessage,
  setHasMore,
  setShowErrorPage,
  addWhatsappChatbotMessage,
  handleCurrentChatDeletion,
  setChatBuckets,
  setCurrentChat,
  setSearchQuery,
  setSelectedChatLoanAndUserDetails,
  setIsAgentOnline,
  setFirebaseSignInLoading,
  setReplySendingStatus,
  setShouldScrollToBottom,
  setQuickFilters,
  resetQuickFilters,
  setTemplateTriggeredDetails,
  setIsFileUploaded,
  setUploadedFileData,
  restoreCurrentChatFromReferenceId,
  setInitializeReferenceIdMap
} = whatsappChatbotSlice.actions;

export default whatsappChatbotSlice.reducer;
